import { useMemo } from "react";
import useCurrentUser from "core/hooks/useCurrentUser";
import useAuthenticatedCall from "core/hooks/useAuthenticatedCall";
import {
  getOrganizationById,
  getOrganizations,
  getUserProfileById,
  getUserProfiles,
  getMemberships,
  getAssignableOrganizationsByEmail,
  getOrganizationOnboarding,
  getCustomerSegments,
  getStandardWorkbooks,
  getCustomWorkbooks,
  getWorkbookEmbedUrl,
  getSavedViews,
} from "modules/admin/actions";
import useHasPermissions from "core/hooks/useHasPermissions";
import {
  PERMISSIONS,
  catalogQueryKey,
  prCatalogQueryKey,
} from "core/constants";
import { getProductCatalog } from "modules/admin/actions";
import { useQuery } from "@tanstack/react-query";
import useActiveMembership from "./hooks/useActiveMembership";

export const useOrganizationListQuery = ({ select, include } = {}) => {
  const { currentUser } = useCurrentUser();

  const fetchQueryCall = useAuthenticatedCall(getOrganizations);

  const meta = useMemo(() => {
    return {
      persist: true,
    };
  }, []);

  const queryKey = useMemo(() => {
    if (include) {
      return ["organizations", { include }];
    }

    return ["organizations"];
  }, [include]);

  const query = useQuery({
    queryKey,
    queryFn: fetchQueryCall,
    enabled: !!currentUser,
    select,
    meta,
  });

  return query;
};

export const useOrganizationByIdQuery = ({
  organizationId,
  select,
  placeholderData = {},
  include,
} = {}) => {
  const { currentUser } = useCurrentUser();
  const fetchQueryCall = useAuthenticatedCall(getOrganizationById);
  const meta = useMemo(() => {
    return {
      persist: true,
    };
  }, []);

  const query = useQuery({
    queryKey: [
      "organizations",
      {
        organizationId,
        include,
      },
    ],
    queryFn: fetchQueryCall,
    placeholderData,
    select,
    enabled: !!currentUser && !!organizationId,
    meta,
  });

  return query;
};

export const useOrganizationOnboardingQuery = ({
  organizationId,
  select,
  enabled = true,
} = {}) => {
  const { currentUser } = useCurrentUser();
  const fetchQueryCall = useAuthenticatedCall(getOrganizationOnboarding);
  const meta = useMemo(() => {
    return {
      persist: true,
    };
  }, []);

  const query = useQuery({
    queryKey: [
      "organization-onboarding",
      {
        organizationId,
      },
    ],
    queryFn: fetchQueryCall,
    select,
    enabled: !!currentUser && organizationId && !!enabled,
    meta,
  });

  return query;
};

export const useOrganizationDeploymentQuery = ({
  organizationId,
  select,
  placeholderData = {},
} = {}) => {
  const { currentUser } = useCurrentUser();
  const fetchQueryCall = useAuthenticatedCall(getOrganizationOnboarding);
  const meta = useMemo(() => {
    return {
      persist: true,
    };
  }, []);

  const query = useQuery({
    queryKey: [
      "organization-deployment",
      {
        organizationId,
      },
    ],
    queryFn: fetchQueryCall,
    placeholderData,
    select,
    enabled: !!currentUser && !!organizationId,
    meta,
  });

  return query;
};

export const useCustomerSegmentListQuery = ({
  select,
  enabled = true,
} = {}) => {
  const { currentUser } = useCurrentUser();
  const fetchQueryCall = useAuthenticatedCall(getCustomerSegments);
  const meta = useMemo(() => {
    return {
      persist: true,
    };
  }, []);

  const query = useQuery({
    queryKey: ["customer-segments"],
    queryFn: fetchQueryCall,
    select,
    enabled: !!currentUser && !!enabled,
    meta,
  });

  return query;
};

export const useProductCatalog = ({ select, isPrCatalog = false } = {}) => {
  const { currentUser } = useCurrentUser();
  const { activeMembership } = useActiveMembership();
  const userHasPermissions = useHasPermissions(
    PERMISSIONS[activeMembership?.role],
    "catalog:read"
  );
  const fetchCatalog = useAuthenticatedCall(getProductCatalog);
  const meta = useMemo(() => {
    return {
      persist: true,
    };
  }, []);

  const { error, data, isLoading } = useQuery({
    queryKey: isPrCatalog ? prCatalogQueryKey : catalogQueryKey,
    queryFn: fetchCatalog,
    staleTime: Infinity,
    enabled: !!currentUser && userHasPermissions,
    select,
    meta,
  });

  return { error, catalog: data, isLoading };
};

export const useUserByIdQuery = ({
  userId,
  select,
  placeholderData,
  enabled = true,
} = {}) => {
  const fetchQueryCall = useAuthenticatedCall(getUserProfileById);

  const meta = useMemo(() => {
    return {
      persist: true,
    };
  }, []);

  const query = useQuery({
    queryKey: [
      "user-profiles",
      {
        userId,
      },
    ],
    queryFn: fetchQueryCall,
    placeholderData,
    select,
    enabled: !!userId && enabled,
    meta,
  });

  return query;
};

export const useMembershipListQuery = ({
  userId,
  orgId,
  select,
  placeholderData = { rows: [], page_info: {} },
  enabled = true,
} = {}) => {
  const { currentUser } = useCurrentUser();
  const fetchQueryCall = useAuthenticatedCall(getMemberships);

  const meta = useMemo(() => {
    return {
      persist: true,
    };
  }, []);

  const query = useQuery({
    queryKey: [
      "memberships",
      {
        userId,
        orgId,
      },
    ],
    queryFn: fetchQueryCall,
    placeholderData,
    select,
    enabled: !!currentUser && enabled,
    meta,
  });

  return query;
};

export const useUserProfilesQuery = ({
  email,
  enabledStatus,
  organizationId,
  select,
  placeholderData = { rows: [], page_info: {} },
  enabled = true,
} = {}) => {
  const { currentUser } = useCurrentUser();
  const fetchQueryCall = useAuthenticatedCall(getUserProfiles);

  const meta = useMemo(() => {
    return {
      persist: false,
      errorMessage: "Error fetching user profiles.",
    };
  }, []);

  const query = useQuery({
    queryKey: [
      "user-profiles",
      {
        email,
        enabledStatus,
        organizationId,
      },
    ],

    queryFn: fetchQueryCall,
    placeholderData,
    select,
    enabled: !!currentUser && enabled,
    meta,
  });

  return query;
};

export const useAssignableOrganizationsListQuery = ({
  email,
  select,
  enabled = true,
  placeholderData = [],
}) => {
  const { currentUser } = useCurrentUser();

  const fetchQueryCall = useAuthenticatedCall(
    getAssignableOrganizationsByEmail
  );

  const meta = useMemo(() => {
    return {
      persist: true,
    };
  }, []);

  const query = useQuery({
    queryKey: [
      "assignable-organizations",
      {
        email,
      },
    ],
    placeholderData,
    queryFn: fetchQueryCall,
    enabled: !!currentUser && enabled,
    select,
    meta,
  });

  return query;
};

/**
 * Analytics Hub Queries
 */

export const useStandardWorkbooksQuery = ({
  organizationId,
  select,
  placeholderData,
  enabled = true,
} = {}) => {
  const { currentUser } = useCurrentUser();
  const fetchQueryCall = useAuthenticatedCall(getStandardWorkbooks);

  const meta = useMemo(() => {
    return {
      persist: true,
    };
  }, []);

  const query = useQuery({
    queryKey: ["workbooks-standard", { organizationId }],
    queryFn: fetchQueryCall,
    placeholderData,
    select,
    enabled: !!currentUser && !!organizationId && enabled,
    staleTime: 1000 * 15,
    meta,
  });

  return query;
};

export const useCustomWorkbooksQuery = ({
  organizationId,
  select,
  placeholderData,
  enabled = true,
} = {}) => {
  const { currentUser } = useCurrentUser();
  const fetchQueryCall = useAuthenticatedCall(getCustomWorkbooks);

  const meta = useMemo(() => {
    return {
      persist: true,
    };
  }, []);

  const query = useQuery({
    queryKey: ["workbooks-custom", { organizationId }],
    queryFn: fetchQueryCall,
    placeholderData,
    select,
    enabled: !!currentUser && !!organizationId && enabled,
    staleTime: 1000 * 15,
    meta,
  });

  return query;
};

/**
 * The Sigma embed url for one workbook, carrying a single-use JWT.
 *
 * @param {object} [options]
 * @param {string} [options.organizationId] Tenant the embed is issued for. Also
 *   gates the fetch — absent, the query stays idle.
 * @param {string} [options.workbookUrlId] Identifies the workbook. Standard
 *   workbooks carry this *and* `workbookCatalogId`; custom ones only this. At
 *   least one is required — both empty and the endpoint gets no identifier.
 * @param {string} [options.workbookCatalogId] Second identifier, standard
 *   workbooks only. Whichever are set are both sent; the backend decides which
 *   wins, so don't rely on an order here.
 * @param {"alpha" | "beta" | "live"} [options.version] Which SDLC tag of the
 *   workbook to embed. Saved views are scoped to one, so switching lands on a
 *   different set.
 * @param {string | null} [options.exploreKey] The *applied* exploration, `null`
 *   for Standard View. The live key would reload the embed on every interaction.
 * @param {string | null} [options.defaultState] The org's default state, applied
 *   client-side when there's no exploration to restore. Keyed so a change buys a
 *   fresh JWT rather than rewriting the url under a loaded iframe.
 * @param {number} [options.reloadToken] Bumped by the caller whenever the iframe
 *   is about to remount, which needs a JWT nobody has spent. The explore key
 *   cannot signal that alone: returning to Standard View after exploring out of
 *   it moves it null-to-null, leaving the query key unchanged and the new iframe
 *   holding an already-used url. Keyed rather than refetched, so an in-flight
 *   fetch for the previous token cannot resolve into this one.
 * @param {boolean} [options.enabled] Holds the fetch until the caller knows which
 *   exploration to ask for. A premature fetch spends a JWT on an iframe load that
 *   never happens.
 * @returns {import("@tanstack/react-query").UseQueryResult<{ embed_url: string }>}
 *   `embed_url` is the only field consumed; the response may carry more.
 */
export const useWorkbookEmbedQuery = ({
  organizationId,
  workbookUrlId,
  workbookCatalogId,
  version,
  exploreKey,
  defaultState = null,
  reloadToken = 0,
  enabled = true,
} = {}) => {
  const { currentUser } = useCurrentUser();
  const fetchQueryCall = useAuthenticatedCall(getWorkbookEmbedUrl);

  const meta = useMemo(() => {
    return {
      persist: false,
      disableBroadcast: true,
    };
  }, []);

  const query = useQuery({
    queryKey: [
      "workbook-embed-url",
      {
        organizationId,
        workbookUrlId,
        workbookCatalogId,
        version,
        exploreKey,
        defaultState,
        reloadToken,
      },
    ],
    // `exploreKey` and `defaultState` are applied client-side, `reloadToken` is
    // local bookkeeping — none go to the endpoint, but all three key the query.
    // Every url input must be here: one that isn't can change the url under an
    // already-authenticated iframe, replaying a spent JWT.
    queryFn: fetchQueryCall,
    // Every fetch mints a JWT, so the identifiers the endpoint needs gate it
    // rather than being sent empty.
    enabled:
      enabled &&
      !!currentUser &&
      !!organizationId &&
      (!!workbookUrlId || !!workbookCatalogId) &&
      !!version,
    // Sigma embed JWTs are single-use — reusing a cached embed_url triggers
    // "The JWT has already been used." So never cache it: drop the query the
    // moment the viewer modal unmounts (gcTime: 0) and always fetch a fresh
    // url + JWT when it reopens.
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: "always",
    meta,
  });

  return query;
};

/**
 * Saved views for one workbook and version — an explore key only restores
 * against the version it came from, so a version switch re-queries.
 */
export const savedViewsQueryKey = ({
  organizationId,
  workbookUrlId,
  version,
}) => ["saved-views", { organizationId, workbookUrlId, version }];

export const useSavedViewsQuery = ({
  organizationId,
  workbookUrlId,
  version,
  select,
  enabled = true,
} = {}) => {
  const { currentUser } = useCurrentUser();
  const fetchQueryCall = useAuthenticatedCall(getSavedViews);

  const meta = useMemo(() => {
    return {
      persist: true,
    };
  }, []);

  const query = useQuery({
    queryKey: savedViewsQueryKey({ organizationId, workbookUrlId, version }),
    queryFn: fetchQueryCall,
    select,
    enabled:
      !!currentUser &&
      !!organizationId &&
      !!workbookUrlId &&
      !!version &&
      enabled,
    staleTime: 1000 * 15,
    meta,
  });

  return query;
};
